import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CardRow from '@/domains/dashboard-management/components/CardRow';
import CardTotal from '@/domains/dashboard-management/components/CardTotal/CardTotal';
import ConfirmModal from '@/domains/dashboard-management/components/ConfirmModal/ConfirmModal';
import {
  useAdminOrderSummary,
  useAdminOrders,
  useCancelAdminOrder,
  useConfirmAdminOrderPayment,
  usePrintAdminOrderLabel,
  useRequestAdminOrderPickup,
} from '@/domains/dashboard-management/hooks/useAdminOrders';
import {
  ORDER_STATUS_OPTIONS,
  orderBadge,
  REPORTED_TRANSFERS,
  TRANSFER_REPORTED_OPTION,
} from '@/domains/dashboard-management/utils/orderStatus';
import { STATUS_COURIER } from '@/domains/mobile-web/constant/listStatusCourier.constant';
import { formatPrice } from '@/domains/mobile-web/utils/price';
import DetailForm from '@/shared/components/ui/DetailForm/DetailForm';
import Pagination from '@/shared/components/ui/Pagination/Pagination';
import { formatDateToDDMonYYYY } from '@/shared/utils/date';
import { useColorStore } from '@/store/colorStore';

const PAGE_SIZE = 10;
const SEARCH_DELAY_MS = 300;

type PendingAction = {
  type: 'cancel' | 'pickup' | 'pay';
  orderId: string;
  /** Konfirmasi Pembayaran: the amount to find on the bank statement, e.g. Rp 135.123 */
  amount?: string;
} | null;

const rupiah = (value: string | number) => `Rp ${formatPrice(Number(value))}`;

const HistoryManagement = () => {
  const navigate = useNavigate();
  const { NEWCOLOR } = useColorStore();
  const [currentPage, setCurrentPage] = useState(1);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [pending, setPending] = useState<PendingAction>(null);

  // The list is asked for once typing pauses; a new keyword starts at page 1
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(search.trim());
      setCurrentPage(1);
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: summary } = useAdminOrderSummary();
  const { data: page, isLoading } = useAdminOrders({
    page: currentPage,
    size: PAGE_SIZE,
    q: query,
    ...(status === TRANSFER_REPORTED_OPTION.value ? REPORTED_TRANSFERS : { status }),
  });
  const cancelOrder = useCancelAdminOrder();
  const requestPickup = useRequestAdminOrderPickup();
  const confirmPayment = useConfirmAdminOrderPayment();
  const printLabel = usePrintAdminOrderLabel();

  const rows = page?.rows ?? [];
  const totalPages = Math.max(1, page?.total_pages ?? 1);
  const busy = cancelOrder.isPending || requestPickup.isPending || confirmPayment.isPending;

  const confirm = () => {
    if (!pending) return;
    const action = { cancel: cancelOrder, pickup: requestPickup, pay: confirmPayment }[
      pending.type
    ];
    action.mutate(pending.orderId, { onSettled: () => setPending(null) });
  };

  return (
    <section className="flex! flex-col gap-6 overflow-auto mb-10">
      <h1 className="capitalize title2medium">Riwayat Transaksi</h1>
      <div className="grid grid-cols-2  md:grid-cols-6 lg:grid-cols-5 xl:grid-cols-5 md:gap-4 lg:gap-6 gap-3">
        <CardTotal
          title="Total Transaksi"
          total={summary?.total ?? '-'}
          unit="Transaksi"
          className="md:col-span-2 lg:col-span-1 "
        />
        <CardTotal
          title="Menunggu Pembayaran"
          total={summary?.pending ?? '-'}
          styleTitle="bg-[#FFDDB8]"
          className="md:col-span-2 lg:col-span-1 "
        />
        <CardTotal
          title="Menunggu Konfirmasi"
          total={summary?.transfer_reported ?? '-'}
          styleTitle="bg-[#FF9B5B]"
          className="md:col-span-2 lg:col-span-1 "
        />
        <CardTotal
          title="Diproses"
          total={summary?.processing ?? '-'}
          className="col-span-1 md:col-span-3 lg:col-span-1"
        />
        <CardTotal
          title="Selesai"
          total={summary?.completed ?? '-'}
          styleTitle="bg-[#99E7CD]"
          className="col-span-2 md:col-span-3 lg:col-span-1"
        />
      </div>

      <div className="shadowCard flex flex-col gap-3">
        <div className="w-full p-3 md:p-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="shrink-0">
            <p className="title1bold capitalize">Daftar Transaksi</p>
            <p className="body1 text-text-grey">{page?.total ?? 0} transaksi ditemukan</p>
          </div>
          <div className="flex flex-col gap-3 w-full sm:flex-row md:flex-1 md:justify-end md:max-w-[640px]">
            <DetailForm
              inputType="select"
              className="w-full sm:w-[180px] sm:shrink-0"
              showTitle={false}
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setCurrentPage(1);
              }}
              optionSelect={[
                { value: '', label: 'Semua Status' },
                // Menunggu Konfirmasi right after Menunggu Pembayaran
                ...ORDER_STATUS_OPTIONS.flatMap((option) =>
                  option.value === 'pending' ? [option, TRANSFER_REPORTED_OPTION] : [option]
                ),
              ]}
              disableEmptyOption={false}
            />
            <DetailForm
              inputType="text"
              placeholder="ID Pembelian, Nomor Invoice atau Status"
              showTitle={false}
              inputFormStyle="store"
              className="w-full sm:flex-1 sm:min-w-0"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className=" divider h-[0.5px] w-full bg-blue-cs-200" />
        <div className="flex-col flex gap-6 p-3 md:p-6 w-full overflow-auto min-h-[200px] max-h-[500px]">
          {rows.length > 0 ? (
            rows.map((transaction) => {
              const reported = transaction.transfer_status === 'reported';
              const badge = STATUS_COURIER[orderBadge(transaction)];
              return (
                <CardRow
                  key={transaction.order_id}
                  code={transaction.order_number}
                  badge={{
                    label: reported ? badge.label : (transaction.status_label ?? badge.label),
                    bg: NEWCOLOR[badge.bg],
                    color: badge.color,
                  }}
                  subtitle={transaction.order_number}
                  fields={[
                    { key: 'buyer', label: 'Pembeli', value: transaction.buyer_name },
                    {
                      key: 'date',
                      label: 'Tanggal',
                      value: formatDateToDDMonYYYY(transaction.placed_at),
                    },
                    {
                      key: 'courier',
                      label: 'Jasa Pengiriman',
                      value: transaction.courier_name ?? '-',
                    },
                    {
                      key: 'trackingNumber',
                      label: 'Resi',
                      value: transaction.tracking_number ?? '-',
                      copyable: Boolean(transaction.tracking_number),
                      // Cetak Resi: the label PDF opens in a new tab
                      isPrint: transaction.can_print_label,
                      onPrint: () => {
                        if (!printLabel.isPending) printLabel.print(transaction.order_id);
                      },
                    },
                  ]}
                  summaries={[
                    {
                      key: 'total',
                      label: 'Total Bayar',
                      // The transferred amount (with the unique code), to match the bank statement
                      value: rupiah(transaction.payment_amount ?? transaction.grand_total),
                    },
                  ]}
                  actions={[
                    {
                      key: 'confirm',
                      label: 'Konfirmasi Pembayaran',
                      variant: 'secondary',
                      disabled: !transaction.can_confirm_payment || busy,
                      onClick: () =>
                        setPending({
                          type: 'pay',
                          orderId: transaction.order_id,
                          amount: rupiah(transaction.payment_amount ?? transaction.grand_total),
                        }),
                    },
                    {
                      key: 'pickup',
                      label: 'Request Jemput',
                      variant: 'primary',
                      disabled: !transaction.can_request_pickup || busy,
                      onClick: () => setPending({ type: 'pickup', orderId: transaction.order_id }),
                    },
                    {
                      key: 'cancel',
                      label: 'Batalkan',
                      variant: 'danger',
                      disabled: !transaction.can_cancel || busy,
                      onClick: () => setPending({ type: 'cancel', orderId: transaction.order_id }),
                    },
                    {
                      key: 'detail',
                      label: 'Detail',
                      variant: 'outline',
                      onClick: () =>
                        navigate(`/dashboard/history-detail?id=${transaction.order_id}`, {
                          state: { code: transaction.order_number },
                        }),
                    },
                  ]}
                />
              );
            })
          ) : (
            <p className="text-center">{isLoading ? 'Memuat...' : 'Tidak ada data'}</p>
          )}
        </div>

        <div className="divider h-[0.5px] w-full bg-blue-cs-200" />
        <div className="w-full p-3 md:p-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="body1 text-text-grey whitespace-nowrap">
            Menampilkan <span className="font-bold text-text-black">{PAGE_SIZE}</span> Data per
            Halaman
          </p>
          <div className="flex  items-center gap-[6px] justify-center m-auto lg:m-0">
            <Pagination
              pageCount={totalPages}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              previousLabel={<ChevronLeft size={16} />}
              nextLabel={<ChevronRight size={16} />}
            />
          </div>
        </div>
      </div>

      <ConfirmModal
        open={pending?.type === 'cancel'}
        title="Batalkan Transaksi ?"
        message="Apakah Anda yakin ingin membatalkan transaksi ini?"
        confirmLabel="Ya, Batalkan"
        variant="danger"
        loading={busy}
        onConfirm={confirm}
        onClose={() => setPending(null)}
      />
      <ConfirmModal
        open={pending?.type === 'pay'}
        title="Konfirmasi Pembayaran ?"
        message={`Pastikan transfer ${pending?.amount ?? ''} sudah masuk ke rekening. Pesanan diproses setelah dikonfirmasi.`}
        confirmLabel="Ya, Konfirmasi"
        variant="success"
        loading={busy}
        onConfirm={confirm}
        onClose={() => setPending(null)}
      />
      <ConfirmModal
        open={pending?.type === 'pickup'}
        title="Konfirmasi Penjemputan ?"
        message="Apakah Anda yakin ingin memproses penjemputan ke lokasi pengirim?"
        confirmLabel="Ya, Jemput"
        variant="primary"
        loading={busy}
        onConfirm={confirm}
        onClose={() => setPending(null)}
      />
    </section>
  );
};

export default HistoryManagement;
